import React, { useMemo } from "react";
import { IonCol, IonRow } from "@ionic/react";
import { addCircle, copyOutline } from "ionicons/icons";
import { Event, EventEntry } from "../../../models";
import { CreateEntryInterface } from "../../../utilities/eventEntry/CreateEventEntryUtils";
import EntrySectionCard from "../../Cards/EntrySectionCard";
import { ChipTitleOption } from "../../Chips/EntryCardChip";
import CollapsingSection from "../../Layout/CollapsingSection";
import { EntrySectionCardSkeleton } from "../../Skeletons/EntrySectionSkeletons";
import { CompactAddActionCard, FullRowAddActionButton, FullWidthAddEntryActions } from "./AddEntryActionButtons";
import { getEntryHeaderLines, getPendingHorseName } from "./entryHeaderUtils";
import { ADD_ACTION_TILE_COUNT, ExpandedCardStates, isAnyEntryExpanded, NEW_ENTRY_SCROLL } from "./newEntryTypes";
import { getParentSectionFocusClassName, handleBlurredSectionClick, ParentSectionBlurOverlay } from "./parentSectionFocus";
import { useCompactEntryGridLayout } from "./useCompactEntryGridLayout";

/**
 * The Entries block of the New Entry page: saved horse cards, unsaved draft slots,
 * copy-in-progress skeletons, and Add/Copy actions (in-grid tiles or the full-width row).
 * Layout measurement lives in `useCompactEntryGridLayout`; CRUD stays on the page.
 */

interface CompactEntryGridProps {
    currentEvent: Event;
    currentEntries: EventEntry[] | undefined | null;
    /** How many unsaved "New Horse" draft cards to show (Add New Entry). */
    extraNewEntryCount: number;
    /** Skeleton cards while Copy Previous Entry is writing. */
    copyingEntryCount: number;
    expandedCard: ExpandedCardStates;
    /** Immediate horse/rider labels while a save is in flight (`null` key = new draft). */
    pendingEntrySubtitles: Map<string | null, string>;
    savingEntryId: string | null | undefined;
    exitingEntryId: string | null;
    isDeleting: boolean;
    entryToDelete: EventEntry | null;
    saveChangesRequestKey: number;
    isLoading: boolean;
    canCopyPreviousEntry: boolean;
    onCollapseOrExpand: (card: ExpandedCardStates) => (isExpand: boolean) => void;
    onSubmitExisting: (entry: EventEntry, entryInfo: CreateEntryInterface | undefined) => void;
    onSubmitNew: (entryInfo: CreateEntryInterface | undefined) => void;
    onDeleteSaved: (entry: EventEntry) => void;
    onDeleteDraft: (draftCardKey: ExpandedCardStates) => void;
    onEntryExitComplete: (entryId: string) => void;
    onAddAnotherEntry: () => void;
    onOpenCopyEntryModal: () => void;
    onUnsavedChangesChange: (hasChanges: boolean) => void;
    onCollapseFromBlurredArea: () => void;
}

const CompactEntryGrid: React.FC<CompactEntryGridProps> = ({
    currentEvent,
    currentEntries,
    extraNewEntryCount,
    copyingEntryCount,
    expandedCard,
    pendingEntrySubtitles,
    savingEntryId,
    exitingEntryId,
    isDeleting,
    entryToDelete,
    saveChangesRequestKey,
    isLoading,
    canCopyPreviousEntry,
    onCollapseOrExpand,
    onSubmitExisting,
    onSubmitNew,
    onDeleteSaved,
    onDeleteDraft,
    onEntryExitComplete,
    onAddAnotherEntry,
    onOpenCopyEntryModal,
    onUnsavedChangesChange,
    onCollapseFromBlurredArea,
}) => {
    const savedEntryCount = currentEntries?.length ?? 0;
    const newEntrySlotCount = extraNewEntryCount;
    const entrySlotCount = savedEntryCount + newEntrySlotCount + copyingEntryCount;
    const entrySectionExpanded = isAnyEntryExpanded(expandedCard);
    /** Collapsed multi-entry view: max 3/row; include the in-grid add tile so every card shares one size. */
    const useCompactEntryGrid = entrySlotCount > 1 && !entrySectionExpanded;
    /** Try extra columns so entries + both add tiles can share a row when they fit. */
    const compactGridTileCount = useCompactEntryGrid ? entrySlotCount + ADD_ACTION_TILE_COUNT : entrySlotCount;
    const addActionsDisabled = Boolean(expandedCard) || copyingEntryCount > 0;

    // Horse names drive column-fit; include pending titles so a save remeasures immediately.
    const compactEntryTitleKey = useMemo(() => {
        const texts: string[] = [];
        if (currentEntries?.length) {
            for (const entry of currentEntries) {
                const pendingHeader = pendingEntrySubtitles.get(entry.id || null);
                const { horseLine } = getEntryHeaderLines(entry, pendingHeader);
                texts.push(horseLine);
            }
        }
        for (let i = 0; i < newEntrySlotCount; i++) {
            texts.push(getPendingHorseName(pendingEntrySubtitles.get(null)));
        }
        return texts.join("|");
    }, [currentEntries, newEntrySlotCount, pendingEntrySubtitles]);

    const { entryGridRef, entryGridColumns, compactTileMinHeightPx, allowBreakLongWords } = useCompactEntryGridLayout({
        isLoading,
        useCompactEntryGrid,
        compactGridTileCount,
        compactEntryTitleKey,
        copyingEntryCount,
    });

    /** Multi-column compact grid uses card-sized add tiles; a single column uses the adjacent button row. */
    const showCompactAddTile = useCompactEntryGrid && entryGridColumns > 1;
    const showFullWidthAddButton = !showCompactAddTile;
    /** Copy starts a new row by itself (Add filled the leftover slot beside an entry). */
    const copyTileSpansFullRow = showCompactAddTile && (entrySlotCount + 1) % entryGridColumns === 0;
    /** Add + Copy occupy their own row together after entries fill every column. */
    const addActionsShareOwnRow = showCompactAddTile && entrySlotCount % entryGridColumns === 0;

    const getFocusClassName = (card: ExpandedCardStates) => getParentSectionFocusClassName(expandedCard, card);
    const onBlurredSectionClick = (card: ExpandedCardStates) => handleBlurredSectionClick(expandedCard, card, onCollapseFromBlurredArea);
    const renderBlurDismissOverlay = (card: ExpandedCardStates) => (
        <ParentSectionBlurOverlay expandedCard={expandedCard} card={card} onDismiss={onCollapseFromBlurredArea} />
    );

    const getEntrySlotClassName = (card: ExpandedCardStates, extra?: string): string => {
        const classes = [getFocusClassName(card)];
        if (useCompactEntryGrid) {
            classes.push("compact-entry-slot--grid");
        }
        if (extra) {
            classes.push(extra);
        }
        return classes.join(" ");
    };

    const compactEntryGridStyle = useCompactEntryGrid
        ? ({ ["--compact-entry-grid-cols" as string]: entryGridColumns } as React.CSSProperties)
        : undefined;

    return (
        <>
            {/* Entry cards: full-width when alone or expanded; max 3/row (fill when fewer) when collapsed */}
            {(savedEntryCount > 0 || newEntrySlotCount > 0 || copyingEntryCount > 0) && (
                <div
                    ref={entryGridRef}
                    className={[useCompactEntryGrid ? "compact-entry-grid" : "", entrySectionExpanded ? "compact-entry-grid--raised" : ""]
                        .filter(Boolean)
                        .join(" ")}
                    style={compactEntryGridStyle}>
                    {/* Saved entries from this user for this event. */}
                    {currentEntries &&
                        currentEntries.length > 0 &&
                        currentEntries.map((currentEntry, index) => {
                            // Determine chipTitle based on statusNote
                            const chipTitle: ChipTitleOption = currentEntry.status === "complete" ? "Completed" : "Incomplete";
                            const pendingHeader = pendingEntrySubtitles.get(currentEntry.id || null);
                            const { horseLine, ridersLine } = getEntryHeaderLines(currentEntry, pendingHeader);
                            const entryCardKey = `entry${index + 1}` as ExpandedCardStates;
                            const isThisEntryExiting = exitingEntryId === currentEntry.id;

                            return (
                                <CollapsingSection
                                    key={currentEntry.id || index}
                                    data-compact-entry-slot
                                    id={NEW_ENTRY_SCROLL.entry(index + 1)}
                                    isExiting={isThisEntryExiting}
                                    onExitComplete={() => onEntryExitComplete(currentEntry.id)}
                                    className={getEntrySlotClassName(entryCardKey)}
                                    onClickCapture={onBlurredSectionClick(entryCardKey)}>
                                    {renderBlurDismissOverlay(entryCardKey)}
                                    <div data-compact-entry-card className="compact-entry-card-inner">
                                        <EntrySectionCard
                                            event={currentEvent}
                                            leadingEmoji="🐴"
                                            title={`Entry ${index + 1}`}
                                            miniTitle={horseLine}
                                            subtitle={ridersLine}
                                            chipTitle={chipTitle}
                                            trashIcon={expandedCard === entryCardKey}
                                            onDelete={() => onDeleteSaved(currentEntry)}
                                            expandButtonTitle="Edit"
                                            isExpand={expandedCard === entryCardKey}
                                            cardContent={expandedCard === entryCardKey ? expandedCard : entryCardKey}
                                            currentEventEntry={currentEntry}
                                            toggleExpansion={onCollapseOrExpand(entryCardKey)}
                                            onSubmit={(entryInfo: CreateEntryInterface | undefined) => {
                                                onSubmitExisting(currentEntry, entryInfo);
                                            }}
                                            isSaving={savingEntryId === currentEntry.id}
                                            isDeleting={(isDeleting || isThisEntryExiting) && entryToDelete?.id === currentEntry.id}
                                            saveRequestKey={saveChangesRequestKey}
                                            onUnsavedChangesChange={onUnsavedChangesChange}
                                            fillAvailableHeight={useCompactEntryGrid}
                                            allowBreakLongWords={allowBreakLongWords}
                                        />
                                    </div>
                                </CollapsingSection>
                            );
                        })}

                    {/* Unsaved draft cards created by Add New Entry (consumed after a successful save). */}
                    {Array.from({ length: newEntrySlotCount }, (_, draftIndex) => {
                        // Only the first draft uses expand key "entry" (matches scroll / summary navigation).
                        const isPrimaryDraft = draftIndex === 0;
                        const draftCardKey = (isPrimaryDraft ? "entry" : `entry${savedEntryCount + draftIndex + 1}`) as ExpandedCardStates;
                        const entryNumber = savedEntryCount + draftIndex + 1;

                        return (
                            <div
                                key={`new-entry-draft-${draftIndex}`}
                                data-compact-entry-slot
                                id={isPrimaryDraft ? NEW_ENTRY_SCROLL.entryNew : NEW_ENTRY_SCROLL.entry(entryNumber)}
                                className={getEntrySlotClassName(draftCardKey)}
                                onClickCapture={onBlurredSectionClick(draftCardKey)}>
                                {renderBlurDismissOverlay(draftCardKey)}
                                <div data-compact-entry-card className="compact-entry-card-inner">
                                    <EntrySectionCard
                                        event={currentEvent}
                                        leadingEmoji="🐴"
                                        title={`Entry ${entryNumber}`}
                                        miniTitle={getPendingHorseName(pendingEntrySubtitles.get(null))}
                                        subtitle={undefined}
                                        chipTitle="Not Started"
                                        trashIcon={expandedCard === draftCardKey}
                                        expandButtonTitle="Start"
                                        isExpand={expandedCard === draftCardKey}
                                        cardContent={expandedCard === draftCardKey ? expandedCard : "entry"}
                                        currentEventEntry={undefined}
                                        toggleExpansion={onCollapseOrExpand(draftCardKey)}
                                        onSubmit={(entryInfo: CreateEntryInterface | undefined) => {
                                            onSubmitNew(entryInfo);
                                        }}
                                        onDelete={() => onDeleteDraft(draftCardKey)}
                                        isSaving={savingEntryId === null}
                                        saveRequestKey={saveChangesRequestKey}
                                        onUnsavedChangesChange={onUnsavedChangesChange}
                                        fillAvailableHeight={useCompactEntryGrid}
                                        allowBreakLongWords={allowBreakLongWords}
                                    />
                                </div>
                            </div>
                        );
                    })}

                    {/* Placeholders while copied entries are being written so the grid doesn't jump. */}
                    {Array.from({ length: copyingEntryCount }, (_, copyIndex) => {
                        const entryNumber = savedEntryCount + newEntrySlotCount + copyIndex + 1;
                        const copySlotKey = `entry${entryNumber}` as ExpandedCardStates;

                        return (
                            <div key={`copying-entry-${copyIndex}`} data-compact-entry-slot className={getEntrySlotClassName(copySlotKey)}>
                                <div data-compact-entry-card className="compact-entry-card-inner">
                                    <EntrySectionCardSkeleton variant="incompleteEntry" fillAvailableHeight={useCompactEntryGrid} />
                                </div>
                            </div>
                        );
                    })}

                    {/* Card-sized add tiles. A leftover action spans the full row at minified-card height. */}
                    {showCompactAddTile &&
                        (addActionsShareOwnRow ? (
                            <div
                                data-compact-entry-slot
                                className="compact-entry-add-pair"
                                style={{ ["--compact-tile-min-height" as string]: `${compactTileMinHeightPx}px` }}>
                                <div className={getEntrySlotClassName(undefined, "compact-entry-slot--fill-height")} onClickCapture={onBlurredSectionClick(undefined)}>
                                    {renderBlurDismissOverlay(undefined)}
                                    <CompactAddActionCard
                                        color="success"
                                        icon={addCircle}
                                        label="Add New Entry"
                                        disabled={addActionsDisabled}
                                        onClick={onAddAnotherEntry}
                                    />
                                </div>
                                <div className={getEntrySlotClassName(undefined, "compact-entry-slot--fill-height")} onClickCapture={onBlurredSectionClick(undefined)}>
                                    {renderBlurDismissOverlay(undefined)}
                                    <CompactAddActionCard
                                        color="primary"
                                        icon={copyOutline}
                                        label="Copy Previous Entry"
                                        disabled={addActionsDisabled || !canCopyPreviousEntry}
                                        onClick={onOpenCopyEntryModal}
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div data-compact-entry-slot className={getEntrySlotClassName(undefined)} onClickCapture={onBlurredSectionClick(undefined)}>
                                    {renderBlurDismissOverlay(undefined)}
                                    <CompactAddActionCard
                                        color="success"
                                        icon={addCircle}
                                        label="Add New Entry"
                                        disabled={addActionsDisabled}
                                        onClick={onAddAnotherEntry}
                                    />
                                </div>
                                <div
                                    data-compact-entry-slot
                                    className={getEntrySlotClassName(undefined, copyTileSpansFullRow ? "compact-entry-slot--full-row" : undefined)}
                                    onClickCapture={onBlurredSectionClick(undefined)}>
                                    {renderBlurDismissOverlay(undefined)}
                                    {copyTileSpansFullRow ? (
                                        <FullRowAddActionButton
                                            color="primary"
                                            icon={copyOutline}
                                            label="Copy Previous Entry"
                                            disabled={addActionsDisabled || !canCopyPreviousEntry}
                                            onClick={onOpenCopyEntryModal}
                                        />
                                    ) : (
                                        <CompactAddActionCard
                                            color="primary"
                                            icon={copyOutline}
                                            label="Copy Previous Entry"
                                            disabled={addActionsDisabled || !canCopyPreviousEntry}
                                            onClick={onOpenCopyEntryModal}
                                        />
                                    )}
                                </div>
                            </>
                        ))}
                </div>
            )}

            {/* Full-width add actions: single entry, expanded section, or add wrapped onto its own row */}
            {showFullWidthAddButton && (
                <IonRow className={`ion-justify-content-center ${getFocusClassName(undefined)}`} onClickCapture={onBlurredSectionClick(undefined)}>
                    {renderBlurDismissOverlay(undefined)}
                    <IonCol size="12" className="ion-text-center p-0">
                        <FullWidthAddEntryActions
                            addActionsDisabled={addActionsDisabled}
                            canCopyPreviousEntry={canCopyPreviousEntry}
                            onAddNewEntry={onAddAnotherEntry}
                            onCopyPreviousEntry={onOpenCopyEntryModal}
                        />
                    </IonCol>
                </IonRow>
            )}
        </>
    );
};

export default CompactEntryGrid;
